import type { Workspace } from '@/shared/stores/workspace.store';

/**
 * Whether to send a person into the onboarding wizard (S03).
 *
 * The API keeps no "onboarded" flag, so a brand counts as new when its owner
 * has only this brand, it has no industry yet (the wizard sets one) and it
 * was created in the last two weeks — an older brand is an existing
 * customer, never pushed into setup. Finishing or skipping the wizard marks
 * the brand done on this device, and the wizard is offered at most once a
 * browser session, so a detour (to Channels, for a bot token) is not bounced
 * back.
 */

const DONE_PREFIX = 'unipostudio.onboarding.done.';
const OFFERED_PREFIX = 'unipostudio.onboarding.offered.';
const NEW_BRAND_MS = 14 * 24 * 60 * 60 * 1000;

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string): void {
  try {
    storage().setItem(key, '1');
  } catch {
    /* private window or blocked storage: the server-side industry check still applies */
  }
}

/** Finishing or skipping the wizard: never offer it again for this brand on this device. */
export function markOnboardingDone(workspaceId: string | undefined): void {
  if (workspaceId) write(() => localStorage, DONE_PREFIX + workspaceId);
}

/** The wizard was offered this session; a later navigation is the person's own choice. */
export function markOnboardingOffered(workspaceId: string): void {
  write(() => sessionStorage, OFFERED_PREFIX + workspaceId);
}

export function shouldOfferOnboarding(
  workspaces: readonly Workspace[],
  current: Workspace | null,
  userId: string | undefined,
  now: number = Date.now(),
): boolean {
  if (!current || !userId || workspaces.length !== 1) return false;
  if (current.ownerId !== userId) return false;
  if (current.industry) return false;
  const created = current.createdAt ? Date.parse(current.createdAt) : NaN;
  if (!Number.isFinite(created) || now - created > NEW_BRAND_MS) return false;
  if (read(() => localStorage, DONE_PREFIX + current.id)) return false;
  if (read(() => sessionStorage, OFFERED_PREFIX + current.id)) return false;
  return true;
}
